import React, { useEffect, useState, useMemo } from "react";
import { FaArrowLeft } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { unwrapString, formatFullName, formatPhone } from "../utils/formatters";
import "../assets/css/ViewCustomer.css";
import "../assets/css/DigixLeads.css";
import { resolveImageUrl } from "../config/apiConfig";
import AddFollowup from "./AddFollowup";
import { SupportSortableHeader } from "./SupportSortableHeader";
import { useResizableColumns } from "../hooks/useResizableColumns";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE } from "../components/TablePagination";
import { getAuthItem } from "../utils/authStorage";
import AssignedToSelect from "../components/AssignedToSelect";

const LEAD_FOLLOWUPS_ROWS_PER_PAGE_KEY = "leadFollowupsRowsPerPage";

const fixFileUrl = (url) => {
  if (!url) return url;
  return resolveImageUrl(url);
};

function ViewJobLead({ leadId, onBack, onEditLead }) {
  const [loading, setLoading] = useState(true);
  const [jobLead, setJobLead] = useState(null);

  const [loadingFollowups, setLoadingFollowups] = useState(false);
  const [followups, setFollowups] = useState([]);
  const [followupSearch, setFollowupSearch] = useState("");
  const [followupDate, setFollowupDate] = useState("");
  const [followupSortColumn, setFollowupSortColumn] = useState("followupDate");
  const [followupSortDirection, setFollowupSortDirection] = useState("desc");
  const [followupPage, setFollowupPage] = useState(1);
  const [followupRowsPerPage, setFollowupRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(LEAD_FOLLOWUPS_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
    return DEFAULT_ROWS_PER_PAGE;
  });
  const [showAddFollowupModal, setShowAddFollowupModal] = useState(false);

  const [editingFollowup, setEditingFollowup] = useState(null);
  const [followupEditForm, setFollowupEditForm] = useState({
    followupDate: "",
    followupType: "",
    remarks: "",
    nextFollowupDate: ""
  });
  const [updatingFollowup, setUpdatingFollowup] = useState(false);
  const [assignUsers, setAssignUsers] = useState([]);
  const [savingAssigned, setSavingAssigned] = useState(false);
  const followupColDefaultWidths = [170, 130, 260, 170, 170, 140];
  const { getColumnStyle: getFollowupColStyle, handleResizeStart: handleFollowupResizeStart } =
    useResizableColumns(followupColDefaultWidths);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(`/job-lead/${leadId}`);
        const jl =
          response.data?.job_lead ||
          response.data?.jobLead ||
          response.data?.job_lead_data ||
          response.data;
        setJobLead(jl || null);
      } catch (err) {
        console.error("Error fetching job lead:", err);
        alert("Failed to load job lead details.");
      } finally {
        setLoading(false);
      }
    };
    if (leadId) load();
  }, [leadId]);

  useEffect(() => {
    let cancelled = false;
    const loadUsers = async () => {
      try {
        const res = await axiosInstance.get("/users", { params: { status: "Active" } });
        const raw = Array.isArray(res.data) ? res.data : res.data?.users || res.data?.data || [];
        if (!cancelled) setAssignUsers(raw);
      } catch {
        if (!cancelled) setAssignUsers([]);
      }
    };
    loadUsers();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch followups for this job lead
  const fetchFollowups = async () => {
    if (!leadId) return;
    setLoadingFollowups(true);
    try {
      const response = await axiosInstance.get("/job-leads-followups");
      const raw = response?.data;
      const arr = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.job_leads_followups)
          ? raw.job_leads_followups
          : Array.isArray(raw?.data)
            ? raw.data
            : [];

      const filtered = arr.filter((f) => {
        const jid =
          f.Job_Lead_Id ??
          f.job_lead_id ??
          f.JobLead_Id ??
          f.jobLead_id ??
          null;
        return jid != null && String(jid) === String(leadId);
      });

      setFollowups(filtered);
    } catch (err) {
      console.error("Error fetching job lead followups:", err);
      setFollowups([]);
    } finally {
      setLoadingFollowups(false);
    }
  };

  useEffect(() => {
    fetchFollowups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const filteredFollowups = useMemo(() => {
    return followups.filter((f) => {
      if (followupSearch) {
        const search = followupSearch.toLowerCase();
        const r = (f.Remarks || f.remarks || "").toLowerCase();
        const t = (f.Followup_Type || f.followup_type || "").toLowerCase();
        if (!r.includes(search) && !t.includes(search)) return false;
      }
      if (followupDate) {
        const baseDate = f.Followup_Date || f.followup_date;
        const fDate = baseDate ? new Date(baseDate).toISOString().split("T")[0] : "";
        if (fDate !== followupDate) return false;
      }
      return true;
    });
  }, [followups, followupSearch, followupDate]);

  const getFollowupSortValue = (f, columnId) => {
    if (columnId === "followupDate") {
      const t = new Date(f.Followup_Date || f.followup_date || "").getTime();
      return Number.isFinite(t) ? t : 0;
    }
    if (columnId === "type") return String(f.Followup_Type || f.followup_type || "").toLowerCase();
    if (columnId === "remarks") return String(f.Remarks || f.remarks || "").toLowerCase();
    if (columnId === "nextFollowupDate") {
      const t = new Date(f.Next_Followup_Date || f.next_followup_date || "").getTime();
      return Number.isFinite(t) ? t : 0;
    }
    if (columnId === "createdAt") {
      const t = new Date(f.Created_At || f.Created_On || f.created_at || f.created_on || "").getTime();
      return Number.isFinite(t) ? t : 0;
    }
    return "";
  };

  const sortedFollowups = useMemo(() => {
    if (!followupSortColumn) return filteredFollowups;
    const mult = followupSortDirection === "desc" ? -1 : 1;
    return [...filteredFollowups].sort((a, b) => {
      const va = getFollowupSortValue(a, followupSortColumn);
      const vb = getFollowupSortValue(b, followupSortColumn);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      const ida = Number(a?.Followup_Id ?? a?.followup_id ?? a?.id ?? 0);
      const idb = Number(b?.Followup_Id ?? b?.followup_id ?? b?.id ?? 0);
      return (ida - idb) * mult;
    });
  }, [filteredFollowups, followupSortColumn, followupSortDirection]);

  const followupTotalPages = Math.max(1, Math.ceil(sortedFollowups.length / followupRowsPerPage));
  const followupStartIdx = (followupPage - 1) * followupRowsPerPage;
  const currentFollowups = sortedFollowups.slice(followupStartIdx, followupStartIdx + followupRowsPerPage);

  useEffect(() => {
    if (followupPage > followupTotalPages && followupTotalPages > 0) {
      setFollowupPage(1);
    }
  }, [followupPage, followupTotalPages, sortedFollowups.length]);

  const handleFollowupRowsPerPageChange = (next) => {
    setFollowupRowsPerPage(next);
    setFollowupPage(1);
    localStorage.setItem(LEAD_FOLLOWUPS_ROWS_PER_PAGE_KEY, String(next));
  };

  const handleFollowupSort = (columnId) => {
    setFollowupPage(1);
    if (followupSortColumn === columnId) {
      setFollowupSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setFollowupSortColumn(columnId);
      setFollowupSortDirection("asc");
    }
  };

  // Convert API ISO datetime to datetime-local value (YYYY-MM-DDTHH:mm)
  const toDateTimeLocal = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "";
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  const handleCloseAddFollowupModal = () => {
    setShowAddFollowupModal(false);
    fetchFollowups();
  };

  const handleEditFollowup = (followup) => {
    // For job leads, the list endpoint already returns all fields we need.
    // Prefill directly from the row to avoid relying on `GET /job-followups/:id`.
    if (!followup || typeof followup !== "object") return;

    setEditingFollowup(followup);
    setFollowupEditForm({
      followupDate: toDateTimeLocal(followup.Followup_Date || followup.followup_date),
      followupType: followup.Followup_Type || followup.Type || followup.followup_type || "",
      remarks: followup.Remarks || followup.remarks || "",
      nextFollowupDate: toDateTimeLocal(followup.Next_Followup_Date || followup.next_followup_date)
    });
  };

  const handleCloseEditFollowup = () => {
    setEditingFollowup(null);
    setFollowupEditForm({ followupDate: "", followupType: "", remarks: "", nextFollowupDate: "" });
  };

  const handleFollowupEditChange = (e) => {
    const { name, value } = e.target;
    setFollowupEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateFollowupSubmit = async (e) => {
    e.preventDefault();
    if (!editingFollowup) return;
    const followupId = editingFollowup.Followup_Id || editingFollowup.followup_id || editingFollowup.id;
    if (!followupId) return;

    setUpdatingFollowup(true);
    try {
      // Backend expects: "YYYY-MM-DD HH:mm:ss"
      const toBackendDateTime = (localValue) => {
        if (!localValue) return null;
        // datetime-local value: "YYYY-MM-DDTHH:mm" (no seconds)
        // If seconds already exist, keep them; otherwise append ":00".
        const hasSeconds = /:\d{2}:\d{2}$/.test(localValue);
        const withSeconds = hasSeconds ? localValue : `${localValue}:00`;
        return withSeconds.replace("T", " ");
      };

      const formattedDate = toBackendDateTime(followupEditForm.followupDate);
      const formattedNext = toBackendDateTime(followupEditForm.nextFollowupDate);

      let updatedBy = 1;
      try {
        const userStr = getAuthItem("user");
        if (userStr) {
          const user = JSON.parse(userStr);
          updatedBy = user.user_id || user.id || 1;
        }
      } catch {
        // ignore
      }

      await axiosInstance.put(`/update-job-followup/${followupId}`, {
        Followup_Date: formattedDate,
        Followup_Id: followupId,
        Followup_Type: followupEditForm.followupType || null,
        Job_Lead_Id: leadId,
        Remarks: followupEditForm.remarks || null,
        Next_Followup_Date: formattedNext,
        Updated_By: updatedBy
      });

      alert("Followup updated successfully.");
      handleCloseEditFollowup();
      fetchFollowups();
    } catch (err) {
      console.error("Error updating job followup:", err);
      alert(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to update followup."
      );
    } finally {
      setUpdatingFollowup(false);
    }
  };

  if (loading) {
    return (
      <div className="viewlead-container">
        <div>Loading job lead details...</div>
      </div>
    );
  }

  if (!jobLead) {
    return (
      <div className="viewlead-container">
        <div>Job lead not found.</div>
        <button className="viewlead-back-link" onClick={onBack}>
          <FaArrowLeft /> Back
        </button>
      </div>
    );
  }

  const address = jobLead?.address || {};
  const v = (x) => unwrapString(x) ?? "";
  const resumeUrl = fixFileUrl(jobLead?.Resume_Url);
  const resumePath = jobLead?.Resume_Path || "";

  const rolesArr = Array.isArray(jobLead?.Roles) ? jobLead.Roles : [];
  const roleNames = rolesArr
    .map((r) => v(r.Role_Name || r.role_name || r.name))
    .filter(Boolean);

  const formatDate = (d) => {
    if (!d) return "—";
    try {
      const dt = new Date(d);
      return Number.isNaN(dt.getTime()) ? "—" : dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
      return "—";
    }
  };

  const formatLeadPhone = (phone, countryCode) => {
    if (!phone) return "";
    const cc = countryCode || jobLead.Country_Code || jobLead.country_code;
    const combined = cc
      ? `${String(cc).startsWith("+") ? cc : `+${cc}`}${String(phone).replace(/\D/g, "")}`
      : phone;
    return formatPhone(combined) || "";
  };

  const personalPhoneDisplay = formatLeadPhone(v(jobLead.Phone) || jobLead.Phone);
  const altPhoneDisplay = formatLeadPhone(v(jobLead.Alt_Phone) || jobLead.Alt_Phone);
  const statusValue = jobLead.Status || jobLead.status || "";

  const getStatusTone = (status) => {
    const value = String(status || "").trim().toLowerCase();
    if (/(won|qualified|converted|completed|active)/.test(value)) return "success";
    if (/(lost|rejected|cancelled|canceled|closed)/.test(value)) return "danger";
    if (/(contacted|follow|pending|progress|warm)/.test(value)) return "warning";
    if (/(new|open|cold)/.test(value)) return "info";
    return "neutral";
  };

  const addressParts = [
    v(address.Address_Line_1),
    v(address.Address_Line_2),
    address.City_Name || "",
    address.State_Name || "",
    address.Country_Name || "",
    v(address.Zip_Code),
  ].filter(Boolean);
  const addressStr = addressParts.length > 0 ? addressParts.join(", ") : null;

  const InfoItem = ({ label, value, href, tone, children }) => {
    if (children != null) {
      return (
        <div className="viewlead-info-item">
          <span className="viewlead-info-label">{label}</span>
          <div className="viewlead-info-value">{children}</div>
        </div>
      );
    }
    const hasValue = value != null && String(value).trim() !== "" && value !== "—";
    const display = hasValue ? value : "—";
    return (
      <div className={`viewlead-info-item ${!hasValue ? "viewlead-info-item--empty" : ""}`}>
        <span className="viewlead-info-label">{label}</span>
        {hasValue && href ? (
          <a
            className="viewlead-info-value viewlead-link"
            href={href}
            onClick={(e) => e.stopPropagation()}
          >
            {display}
          </a>
        ) : tone && hasValue ? (
          <span className={`viewlead-info-value viewlead-status-pill viewlead-status-pill--${tone}`}>
            {display}
          </span>
        ) : (
          <span className={`viewlead-info-value ${!hasValue ? "muted" : ""}`}>
            {display}
          </span>
        )}
      </div>
    );
  };

  const handleAssignedToChange = async (userId) => {
    if (!leadId) return;
    setSavingAssigned(true);
    try {
      const payload = {
        Assigned_To: userId === "" || userId == null ? null : Number(userId),
      };
      const res = await axiosInstance.put(`/update-job-lead/${leadId}`, payload);
      const jl = res.data?.job_lead || res.data;
      setJobLead((prev) => ({
        ...(prev || {}),
        ...(jl && typeof jl === "object" ? jl : {}),
        Assigned_To: payload.Assigned_To,
      }));
    } catch (err) {
      alert(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Failed to update Assigned To."
      );
    } finally {
      setSavingAssigned(false);
    }
  };

  return (
    <div className="viewlead-container">
      <div className="viewlead-header">
        <button className="viewlead-back-link" onClick={onBack}>
          <FaArrowLeft /> Back to Job Leads
        </button>
      </div>

      <div className="viewlead-card">
        <div className="viewlead-section-header viewlead-personal-section-header">
          <h3 className="viewlead-section-title no-border">Personal Information</h3>
          <div className="viewlead-section-actions">
            <button className="btn-edit-lead" onClick={() => onEditLead && onEditLead(leadId, "job-lead")}>
              Edit Job Lead
            </button>
          </div>
        </div>
        <div className="viewlead-info-grid">
          <div className="viewlead-info-item viewlead-info-item--name-status">
            <span className="viewlead-info-label">Full Name</span>
            <div className="viewlead-name-status-row">
              {(() => {
                const name = formatFullName(null, jobLead.First_Name, jobLead.Last_Name);
                return (
                  <span className={`viewlead-info-value ${!name || name === "—" ? "muted" : ""}`}>
                    {name && name !== "—" ? name : "—"}
                  </span>
                );
              })()}
              {statusValue ? (
                <span className={`viewlead-status-pill viewlead-status-pill--${getStatusTone(statusValue)}`}>
                  {statusValue}
                </span>
              ) : null}
            </div>
          </div>
          <InfoItem
            label="Personal Email"
            value={v(jobLead.Email)}
            href={v(jobLead.Email) ? `mailto:${v(jobLead.Email)}` : undefined}
          />
          <InfoItem
            label="Personal Phone"
            value={personalPhoneDisplay || v(jobLead.Phone)}
            href={
              (personalPhoneDisplay || jobLead.Phone)
                ? `tel:${String(personalPhoneDisplay || jobLead.Phone).replace(/[^\d+]/g, "")}`
                : undefined
            }
          />
          <InfoItem label="Alternate phone" value={altPhoneDisplay || v(jobLead.Alt_Phone)} />
        </div>

        <h3 className="viewlead-section-title">Role & Experience</h3>
        <div className="viewlead-info-grid">
          <InfoItem label="Qualification" value={jobLead.Qualification_Name || jobLead.qualification_name || jobLead.Qualification_Id || ""} />
          <InfoItem label="Experience Years" value={jobLead.Experience_Years ?? ""} />
          <InfoItem label="Current Company" value={v(jobLead.Current_Company)} />
          <InfoItem label="Designation" value={jobLead.Designation_Name || jobLead.designation_name || jobLead.Designation_Id || ""} />
          <InfoItem label="Skills" value={v(jobLead.Skills)} />
          <InfoItem label="Preferred Location" value={v(jobLead.Preferred_Location)} />
        </div>

        <h3 className="viewlead-section-title">Compensation</h3>
        <div className="viewlead-info-grid">
          <InfoItem label="Expected Salary" value={v(jobLead.Expected_Salary)} />
          <InfoItem label="Current Salary" value={v(jobLead.Current_Salary)} />
          <InfoItem label="Source" value={jobLead.Source_Name || jobLead.Source_Id || ""} />
          <InfoItem label="Assigned To">
            <AssignedToSelect
              value={jobLead.Assigned_To ?? ""}
              users={assignUsers}
              disabled={savingAssigned}
              onChange={handleAssignedToChange}
            />
          </InfoItem>
          <InfoItem label="Roles" value={roleNames.length ? roleNames.join(", ") : ""} />
          <InfoItem label="Created On" value={formatDate(jobLead.Created_On)} />
          <InfoItem label="Updated On" value={formatDate(jobLead.Updated_On)} />
        </div>

        <h3 className="viewlead-section-title">Address</h3>
        {addressStr ? (
          <p className="viewlead-address-value">{addressStr}</p>
        ) : (
          <p className="viewlead-address-text">—</p>
        )}

        <h3 className="viewlead-section-title">Resume</h3>
        <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
          {resumeUrl ? (
            <a className="viewlead-info-value viewlead-link" href={resumeUrl} target="_blank" rel="noreferrer">
              View Resume
            </a>
          ) : (
            <span className="muted-text">—</span>
          )}
          {!resumeUrl && resumePath ? <span style={{ color: "#7A8CA5", fontStyle: "italic" }}>{resumePath}</span> : null}
        </div>
      </div>

      {/* Followups Section */}
      <div className="viewlead-section">
        <div className="viewlead-section-header">
          <h3>Followups</h3>
          <div className="viewlead-section-actions">
            <input
              type="text"
              className="viewlead-search-input"
              placeholder="Search by remarks or type..."
              value={followupSearch}
              onChange={(e) => {
                setFollowupSearch(e.target.value);
                setFollowupPage(1);
              }}
            />
            <input
              type="date"
              className="viewlead-date-input"
              value={followupDate}
              onChange={(e) => {
                setFollowupDate(e.target.value);
                setFollowupPage(1);
              }}
            />
            <button
              className="btn-add-followup"
              onClick={() => setShowAddFollowupModal(true)}
              title="New Followup"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              New Followup
            </button>
          </div>
        </div>

        {loadingFollowups ? (
          <p className="viewlead-no-data">Loading followups...</p>
        ) : sortedFollowups.length > 0 ? (
          <>
          <table className="viewlead-table viewlead-followups-table">
            <thead>
              <tr>
                <th className="viewlead-th-sortable" style={getFollowupColStyle(0)}>
                  <div className="viewlead-th-sort-row">
                    <SupportSortableHeader columnId="followupDate" label="Followup Date" sortColumn={followupSortColumn} sortDirection={followupSortDirection} onSort={handleFollowupSort} />
                  </div>
                  <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleFollowupResizeStart(0, e); }} />
                </th>
                <th className="viewlead-th-sortable" style={getFollowupColStyle(1)}>
                  <div className="viewlead-th-sort-row">
                    <SupportSortableHeader columnId="type" label="Type" sortColumn={followupSortColumn} sortDirection={followupSortDirection} onSort={handleFollowupSort} />
                  </div>
                  <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleFollowupResizeStart(1, e); }} />
                </th>
                <th className="viewlead-th-sortable" style={getFollowupColStyle(2)}>
                  <div className="viewlead-th-sort-row">
                    <SupportSortableHeader columnId="remarks" label="Remarks" sortColumn={followupSortColumn} sortDirection={followupSortDirection} onSort={handleFollowupSort} />
                  </div>
                  <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleFollowupResizeStart(2, e); }} />
                </th>
                <th className="viewlead-th-sortable" style={getFollowupColStyle(3)}>
                  <div className="viewlead-th-sort-row">
                    <SupportSortableHeader columnId="nextFollowupDate" label="Next Followup Date" sortColumn={followupSortColumn} sortDirection={followupSortDirection} onSort={handleFollowupSort} />
                  </div>
                  <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleFollowupResizeStart(3, e); }} />
                </th>
                <th className="viewlead-th-sortable" style={getFollowupColStyle(4)}>
                  <div className="viewlead-th-sort-row">
                    <SupportSortableHeader columnId="createdAt" label="Created At" sortColumn={followupSortColumn} sortDirection={followupSortDirection} onSort={handleFollowupSort} />
                  </div>
                  <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleFollowupResizeStart(4, e); }} />
                </th>
                <th style={{ ...getFollowupColStyle(5), textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentFollowups.map((f, idx) => {
                const followupId = f.Followup_Id || f.followup_id || f.id;
                return (
                  <tr key={followupId || idx}>
                    <td>{formatDateTime(f.Followup_Date || f.followup_date)}</td>
                    <td>{f.Followup_Type || f.followup_type || "—"}</td>
                    <td>{f.Remarks || f.remarks || "—"}</td>
                    <td className={!f.Next_Followup_Date && !f.next_followup_date ? "muted-cell" : ""}>
                      {f.Next_Followup_Date || f.next_followup_date
                        ? formatDateTime(f.Next_Followup_Date || f.next_followup_date)
                        : "—"}
                    </td>
                    <td className="blue-cell">
                      {formatDateTime(f.Created_At || f.Created_On || f.created_at || f.created_on)}
                    </td>
                    <td>
                      <div className="viewlead-followup-actions">
                        {followupId ? (
                          <button
                            type="button"
                            className="btn-edit-followup"
                            onClick={() => handleEditFollowup(f)}
                            disabled={updatingFollowup}
                          >
                            Edit
                          </button>
                        ) : (
                          <span className="muted-text">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <TablePagination
            currentPage={followupPage}
            totalItems={sortedFollowups.length}
            rowsPerPage={followupRowsPerPage}
            rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            onPageChange={setFollowupPage}
            onRowsPerPageChange={handleFollowupRowsPerPageChange}
            itemLabel="followups"
            rowsPerPageSelectId="job-lead-followups-rows-per-page"
          />
          </>
        ) : (
          <p className="viewlead-no-data">No followups found.</p>
        )}
      </div>

      {showAddFollowupModal && (
        <div
          className="viewlead-modal-overlay viewlead-addfollowup-overlay"
          onClick={() => setShowAddFollowupModal(false)}
        >
          <div
            className="viewlead-modal-content viewlead-addfollowup-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="viewlead-modal-header">
              <h3 className="viewlead-modal-title">New Followup</h3>
              <button
                type="button"
                className="viewlead-modal-close"
                onClick={() => setShowAddFollowupModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="viewlead-addfollowup-modal-body">
              <AddFollowup
                id={leadId}
                type="job-lead"
                onBack={handleCloseAddFollowupModal}
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit Followup Modal */}
      {editingFollowup && (
        <div
          className="viewlead-modal-overlay"
          onClick={() => !updatingFollowup && handleCloseEditFollowup()}
        >
          <div
            className="viewlead-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="viewlead-modal-header">
              <h3 className="viewlead-modal-title">Edit Followup</h3>
              <button
                type="button"
                className="viewlead-modal-close"
                onClick={handleCloseEditFollowup}
                disabled={updatingFollowup}
              >
                &times;
              </button>
            </div>

            <form
              onSubmit={handleUpdateFollowupSubmit}
              className="viewlead-form-inner"
            >
              <div className="viewlead-form-group">
                <label>
                  Followup Date <span className="required">*</span>
                </label>
                <input
                  type="datetime-local"
                  name="followupDate"
                  value={followupEditForm.followupDate}
                  onChange={handleFollowupEditChange}
                  required
                  disabled={updatingFollowup}
                />
              </div>

              <div className="viewlead-form-group">
                <label>Followup Type</label>
                <input
                  type="text"
                  name="followupType"
                  value={followupEditForm.followupType}
                  onChange={handleFollowupEditChange}
                  placeholder="e.g., Call, Meeting, WhatsApp"
                  disabled={updatingFollowup}
                />
              </div>

              <div className="viewlead-form-group">
                <label>Remarks</label>
                <textarea
                  name="remarks"
                  value={followupEditForm.remarks}
                  onChange={handleFollowupEditChange}
                  rows={3}
                  disabled={updatingFollowup}
                />
              </div>

              <div className="viewlead-form-group">
                <label>Next Followup Date</label>
                <input
                  type="datetime-local"
                  name="nextFollowupDate"
                  value={followupEditForm.nextFollowupDate}
                  onChange={handleFollowupEditChange}
                  disabled={updatingFollowup}
                />
              </div>

              <div className="viewlead-modal-actions">
                <button type="submit" className="btn-submit" disabled={updatingFollowup}>
                  {updatingFollowup ? "Updating..." : "Update Followup"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ViewJobLead;


